import { useCallback, useRef, useState } from "react";
import { ApiAuth, ApiError } from "../../../lib/api";
import { createPaymentAttempt, outcomeOfError } from "../Billing/paymentAttempt";
import { creditsApi } from "./api";

// Adding credit charges the patient's card, and one "Add" the person means to
// make once is one ATTEMPT (Billing/paymentAttempt.ts): the same amount and
// card tried again after a network or server failure carries the same key, so
// the server and Stripe answer it once and the card is never charged twice.
//
// The key covers the CHARGE. Once the server has answered it — charged, or
// asking for 3-D Secure — everything after is about that one PaymentIntent:
// confirming it, then recording it as credit. A charge that went through but
// could not be recorded is recorded again, never charged again: the next
// "Add" retries the recording of the same PaymentIntent (the server records an
// intent once, however often it is asked).

export const SAME_ATTEMPT_RETRY =
  "We couldn't add the credit. Please try again — you won't be charged twice.";
export const RECORD_RETRY =
  "Your card payment went through, but we couldn't add the credit yet. Try again — you won't be charged again.";

/** What confirming a 3-D Secure challenge came to. */
export type CardConfirmation = {
  succeeded: boolean;
  paymentIntentId?: string | null;
  message?: string | null;
};

/** A charge that went through and is not yet recorded as credit. */
export type ChargedNotRecorded = { paymentIntentId: string; amountCents: number };

type Options = {
  auth: ApiAuth;
  locationId: string | null;
  organizationId: string | null;
  /** Runs the card's 3-D Secure challenge for `clientSecret`. */
  confirmCard: (clientSecret: string) => Promise<CardConfirmation>;
  onAdded: (addedCents: number) => void;
  /** The balance may have changed under the person: read it again. */
  onBalanceMayHaveChanged: () => void;
};

export function useAddCredit({
  auth,
  locationId,
  organizationId,
  confirmCard,
  onAdded,
  onBalanceMayHaveChanged,
}: Options) {
  const attempt = useRef(createPaymentAttempt());
  const [charged, setCharged] = useState<ChargedNotRecorded | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const record = async (intent: ChargedNotRecorded) => {
    try {
      await creditsApi.finalizeStripe(auth, {
        paymentIntentId: intent.paymentIntentId,
        amountCents: intent.amountCents,
        locationId,
        organizationId,
      });
    } catch (error) {
      const apiErr = error as ApiError;
      if (outcomeOfError(apiErr) === "retry_same") {
        setCharged(intent);
        setErrMsg(RECORD_RETRY);
      } else {
        setCharged(null);
        onBalanceMayHaveChanged();
        setErrMsg(apiErr?.message || "Could not add credit.");
      }
      return;
    }
    setCharged(null);
    onAdded(intent.amountCents);
  };

  const submit = async ({ amountCents, paymentMethodId }: { amountCents: number; paymentMethodId: string }) => {
    if (submitting) return;
    setSubmitting(true);
    setErrMsg(null);
    try {
      if (charged) {
        await record(charged);
        return;
      }

      const selection = { amountCents, paymentMethodId, locationId, organizationId };
      const key = attempt.current.keyFor(selection);
      let res;
      try {
        res = await creditsApi.issueStripe(auth, selection, key);
      } catch (error) {
        const apiErr = error as ApiError;
        const outcome = outcomeOfError(apiErr);
        attempt.current.settle(outcome);
        if (outcome === "check_first") {
          // The server could not confirm whether the card was charged: say so
          // in its words and read the balance again. Never retried on its own.
          onBalanceMayHaveChanged();
          setErrMsg(apiErr.message);
        } else if (outcome === "retry_same") {
          setErrMsg(SAME_ATTEMPT_RETRY);
        } else {
          setErrMsg(apiErr?.message || "Could not add credit.");
        }
        return;
      }
      attempt.current.settle("success");

      if (res.status === "succeeded") {
        onAdded(amountCents);
        return;
      }
      if (!res.clientSecret) {
        setErrMsg("Payment needs confirmation but no secret was returned.");
        return;
      }
      const confirmation = await confirmCard(res.clientSecret);
      if (!confirmation.succeeded) {
        setErrMsg(confirmation.message || "Payment not completed — please try again.");
        return;
      }
      const paymentIntentId = res.paymentIntentId || confirmation.paymentIntentId;
      if (!paymentIntentId) {
        setErrMsg("Payment went through but could not be identified. Contact the clinic before trying again.");
        return;
      }
      await record({ paymentIntentId, amountCents });
    } catch (error) {
      setErrMsg((error as Error)?.message || "Could not add credit.");
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = useCallback(() => setErrMsg(null), []);

  return { submit, submitting, errMsg, charged, clearError };
}
